import { useEffect, useRef, useState } from "react";
import { LayoutTemplate, Star, Trash2, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Doc } from "./PageBuilder";

export type Template = { id: string; name: string; doc: Doc; is_default: boolean };

const table = () => supabase.from("article_templates" as never);

export const loadTemplates = async (): Promise<Template[]> => {
  const { data } = await table().select("id,name,doc,is_default").order("created_at", { ascending: true });
  return (data as unknown as Template[]) ?? [];
};

/** Dropdown to pick a saved layout; optionally save the current builder structure. */
const TemplateMenu = ({ onApply, currentDoc, compact }: { onApply: (d: Doc) => void; currentDoc?: Doc; compact?: boolean }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Template[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  const refresh = () => loadTemplates().then(setItems);
  useEffect(() => { if (open) refresh(); }, [open]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const save = async () => {
    if (!currentDoc) return;
    const name = window.prompt("Nome do layout:");
    if (!name?.trim()) return;
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) return;
    const { error } = await table().insert({ name: name.trim(), doc: currentDoc, user_id: user.id } as never);
    if (error) toast({ title: "Erro ao salvar layout", description: error.message, variant: "destructive" });
    else { toast({ title: "Layout salvo" }); refresh(); }
  };

  const toggleDefault = async (t: Template) => {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) return;
    await table().update({ is_default: false } as never).eq("user_id", user.id);
    if (!t.is_default) await table().update({ is_default: true } as never).eq("id", t.id);
    toast({ title: t.is_default ? "Padrão removido" : `"${t.name}" agora é seu padrão` });
    refresh();
  };

  const remove = async (t: Template) => {
    if (!window.confirm(`Excluir o layout "${t.name}"?`)) return;
    await table().delete().eq("id", t.id);
    refresh();
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="Layouts salvos"
        className={`flex items-center gap-1.5 rounded hover:bg-muted transition-colors ${compact ? "p-2" : "px-3 py-1.5 text-sm border border-border"}`}
      >
        <LayoutTemplate size={16} />{!compact && "Layouts"}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 w-72 rounded-xl border border-border bg-background shadow-lg p-2 text-sm">
          {items.length === 0 && <p className="text-muted-foreground text-xs p-2">Nenhum layout salvo. Monte uma estrutura no Construtor e salve.</p>}
          {items.map((t) => (
            <div key={t.id} className="flex items-center gap-1 rounded-lg hover:bg-muted/60 group">
              <button type="button" onClick={() => { onApply(t.doc); setOpen(false); }} className="flex-1 text-left px-2 py-1.5 truncate">
                {t.name}
              </button>
              <button type="button" onClick={() => toggleDefault(t)} title={t.is_default ? "Remover como padrão" : "Definir como padrão"} className="p-1.5">
                <Star size={14} className={t.is_default ? "fill-primary text-primary" : "text-muted-foreground"} />
              </button>
              <button type="button" onClick={() => remove(t)} title="Excluir" className="p-1.5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {currentDoc && (
            <button type="button" onClick={save} className="mt-1 w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border-t border-border text-primary hover:bg-primary/10">
              <Save size={14} /> Salvar estrutura atual como layout
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateMenu;
